import React, { useState } from "react";
import { compactPrice } from "@/helpers/compactPrice";
import { milesLabel, slugify } from "@/components/DirectoryBreadcrumb";
import "./AgentDirectory.scss";
import AppLink from "@/components/AppLink";

const DEMAND_CARD_AFTER = 6;

const initialsOf = (name) =>
  String(name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();

export const placeLabel = (location) =>
  [location?.city, location?.state].filter(Boolean).join(", ");

const TONES = 5;

export const toneOf = (name) =>
  String(name || "")
    .split("")
    .reduce((sum, char) => sum + char.charCodeAt(0), 0) % TONES;

export const locationLine = (location, placeName) => {
  if (!location?.town) return "";
  const sameTown = placeName && slugify(location.town) === slugify(placeName);
  if (sameTown || location.miles === null || location.miles === undefined) return `In ${location.town}`;
  return `${location.town}, ${milesLabel(location.miles)}`;
};

const hasDemand = (agent) => agent.activeDemand > 0 || agent.allTimeDemand > 0;

const AgentCard = ({ agent, placeName, hideCompany = false }) => (
  <AppLink className="agent-directory__card" href={`/agent-info/${agent.slug}`}>
    <span className="agent-directory__top">
      <span className={`agent-directory__avatar-wrap${agent.isPro ? " is-pro" : ""}`}>
        <span className={`agent-directory__avatar${agent.photo ? "" : ` is-tone-${toneOf(agent.name)}`}`}>
          {agent.photo ? <img src={agent.photo} alt="" loading="lazy" /> : <span>{initialsOf(agent.name)}</span>}
        </span>
      </span>
      <span className="agent-directory__who">
        <span className="agent-directory__name">{agent.name}</span>
        {agent.company && !hideCompany ? <span className="agent-directory__company">{agent.company}</span> : null}
        {placeName && locationLine(agent.location, placeName) ? (
          <span className="agent-directory__where">{locationLine(agent.location, placeName)}</span>
        ) : null}
        {agent.hasActiveBuyers ? (
          <span className="agent-directory__note is-active">Working with buyers here now</span>
        ) : null}
      </span>
    </span>
    {hasDemand(agent) ? (
      <span className="agent-directory__stats">
        <span>
          {agent.activeDemand > 0 ? (
            <strong>{compactPrice(agent.activeDemand)}</strong>
          ) : (
            <strong className="is-none">None now</strong>
          )}
          Active demand
        </span>
        <span>
          <strong>{compactPrice(agent.allTimeDemand)}</strong>
          All-time demand
        </span>
      </span>
    ) : null}
  </AppLink>
);

const SkeletonCard = () => (
  <span className="agent-directory__card is-loading" aria-hidden="true">
    <span className="agent-directory__top">
      <span className="agent-directory__avatar" />
      <span className="agent-directory__who">
        <span className="agent-directory__bar" style={{ width: "62%" }} />
        <span className="agent-directory__bar" style={{ width: "48%" }} />
        <span className="agent-directory__bar" style={{ width: "70%" }} />
      </span>
    </span>
  </span>
);

const DemandCard = ({ place, onSeeDemand }) => (
  <div className="agent-directory__demand">
    <h3>Selling a home{place ? ` in ${place}` : ""}?</h3>
    <p>Local agents are working with buyers right now. Add your home to see the real-time demand for&nbsp;it.</p>
    <button type="button" className="agent-directory__demand-cta" onClick={onSeeDemand}>
      See demand for my home
    </button>
  </div>
);

const MarketChips = ({ markets, onPick }) =>
  markets?.length ? (
    <div className="agent-directory__markets">
      {markets.map((market) =>
        market.href ? (
          <AppLink key={market.displayName} className="agent-directory__market" href={market.href}>
            {market.image ? <img src={market.image} alt="" /> : null}
            {market.displayName}
          </AppLink>
        ) : (
          <button type="button" key={market.displayName} className="agent-directory__market" onClick={() => onPick(market)}>
            {market.image ? <img src={market.image} alt="" /> : null}
            {market.displayName}
          </button>
        ),
      )}
    </div>
  ) : null;

export const DirectoryPanel = ({ title, body, actions = [] }) => (
  <section className="agent-directory agent-directory--panel">
    <div className="agent-directory__panel">
      <h2>{title}</h2>
      {body ? <p>{body}</p> : null}
      {actions.length ? (
        <div className="agent-directory__actions">
          {actions.map((action, index) => {
            const className = index === 0 ? "agent-directory__primary" : "agent-directory__secondary";
            return action.href ? (
              <AppLink key={action.label} className={className} href={action.href}>
                {action.label}
              </AppLink>
            ) : (
              <button key={action.label} type="button" className={className} onClick={action.onClick}>
                {action.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  </section>
);

export const DirectorySkeleton = ({ count = 9 }) => (
  <section className="agent-directory" aria-busy="true">
    <div className="agent-directory__grid">
      {Array.from({ length: count }, (_, index) => (
        <SkeletonCard key={index} />
      ))}
    </div>
  </section>
);

export const Panel = ({ title, children }) => (
  <section className="agent-directory agent-directory--panel">
    <div className="agent-directory__panel">
      <h2>{title}</h2>
      {children}
    </div>
  </section>
);

const AgentDirectory = ({
  query,
  location,
  scopeLabel,
  placeName,
  filtered = false,
  onClearFilters,
  onSeeDemand,
  markets,
  onPickMarket,
  groupAgents,
  filteredEmpty,
  hideCompany = false,
}) => {
  const [openGroups, setOpenGroups] = useState({});
  const pages = query?.data?.pages || [];
  const first = pages[0];
  // Pages are offset-based over a live ranking, so a later page can repeat an agent.
  const seen = new Set();
  const agents = pages
    .flatMap((page) => page?.agents || [])
    .filter((agent) => !seen.has(agent.slug) && seen.add(agent.slug));
  const place = scopeLabel || placeLabel(location) || "this area";

  if ((!scopeLabel && !location?.latitude) || query?.isLoading) {
    return <DirectorySkeleton />;
  }

  if (query?.isError && !first) {
    return (
      <Panel title="The directory did not load">
        <p>Check your connection and try again. Nothing you searched is lost.</p>
        <div className="agent-directory__actions">
          <button type="button" className="agent-directory__primary" onClick={() => query.refetch()}>
            Try again
          </button>
        </div>
      </Panel>
    );
  }

  if (first?.isEmpty || !agents.length) {
    if (filteredEmpty) {
      return (
        <Panel title={filteredEmpty.title}>
          <p>{filteredEmpty.body}</p>
          <div className="agent-directory__actions">
            <button type="button" className="agent-directory__primary" onClick={filteredEmpty.onAction}>
              {filteredEmpty.actionLabel}
            </button>
          </div>
        </Panel>
      );
    }
    return filtered ? (
      <Panel title={`No agents match in ${place}`}>
        <p>Clear the filters to see every agent here, or look in another market.</p>
        <div className="agent-directory__actions">
          <button type="button" className="agent-directory__primary" onClick={onClearFilters}>
            Clear filters
          </button>
        </div>
        <MarketChips markets={markets} onPick={onPickMarket} />
      </Panel>
    ) : (
      <Panel title={`No agents listed in ${place} yet`}>
        <p>Gitcha lists agents from the MLSs and associations it partners with. These markets are live&nbsp;now.</p>
        <MarketChips markets={markets} onPick={onPickMarket} />
      </Panel>
    );
  }

  const gridClass = `agent-directory__grid${query.isPlaceholderData ? " is-stale" : ""}`;
  const groups = groupAgents
    ? groupAgents(agents).filter((group) => group.agents.length || group.emptyNote)
    : null;
  const closedGroup = groups?.some((group) => group.collapsible && !openGroups[group.key]);

  return (
    <section className="agent-directory" aria-busy={query.isFetching}>
      {groups ? (
        groups.map((group) => {
          const open = !group.collapsible || Boolean(openGroups[group.key]);
          return (
            <div key={group.key} className="agent-directory__group">
              {group.collapsible ? (
                <button
                  type="button"
                  className="agent-directory__toggle"
                  aria-expanded={open}
                  onClick={() => setOpenGroups({ ...openGroups, [group.key]: !open })}
                >
                  {open ? group.hideLabel : group.showLabel}
                  <span className="agent-directory__chevron" aria-hidden="true" />
                </button>
              ) : group.title && (groups.length > 1 || group.always) ? (
                <div className="agent-directory__group-head">
                  <h2>{group.title}</h2>
                  {group.note ? <p>{group.note}</p> : null}
                </div>
              ) : null}
              {!open ? null : group.agents.length ? (
                <div className={gridClass}>
                  {group.agents.map((agent) => (
                    <AgentCard key={agent.slug} agent={agent} placeName={placeName} hideCompany={hideCompany} />
                  ))}
                </div>
              ) : (
                <p className="agent-directory__empty-note">{group.emptyNote}</p>
              )}
            </div>
          );
        })
      ) : (
        <div className={gridClass}>
          {agents.map((agent, index) => (
            <React.Fragment key={agent.slug}>
              <AgentCard agent={agent} placeName={placeName} hideCompany={hideCompany} />
              {index === DEMAND_CARD_AFTER - 1 && onSeeDemand ? (
                <DemandCard place={place} onSeeDemand={onSeeDemand} />
              ) : null}
            </React.Fragment>
          ))}
        </div>
      )}
      {query.isFetchNextPageError ? (
        <p className="agent-directory__more-error" role="alert">
          Couldn't load more agents.
        </p>
      ) : null}
      {query.hasNextPage && !closedGroup ? (
        <button
          type="button"
          className="agent-directory__more"
          disabled={query.isFetchingNextPage}
          onClick={() => query.fetchNextPage()}
        >
          {query.isFetchingNextPage ? "Loading" : query.isFetchNextPageError ? "Try again" : "Show more agents"}
        </button>
      ) : null}
    </section>
  );
};

export default AgentDirectory;
